import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Baby, CalendarDays, Mail, Phone, Search, ShieldCheck, UsersRound } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BottomSheet, Empty, fmtData } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

const RELAZIONI = {
  madre: "Madre",
  padre: "Padre",
  tutore: "Tutore",
  altro_esercente: "Altro esercente",
};

const statoRichiesta = {
  in_attesa: ["In attesa", "badge-warn"],
  approvata: ["Approvata", "badge-pos"],
  rifiutata: ["Rifiutata", "badge-neg"],
};

export default function AccountGenitori() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/genitori/account");
      setAccounts(data || []);
      setError("");
      setSelected((current) => current ? (data || []).find((item) => item.id === current.id) || null : null);
    } catch (err) {
      setError(errMsg(err, "Non riesco a caricare gli account Genitore"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return accounts;
    return accounts.filter((account) => {
      const children = (account.animati || []).map((child) => `${child.nome} ${child.cognome}`).join(" ");
      return `${account.nome} ${account.cognome} ${account.email || ""} ${account.telefono || ""} ${children}`.toLowerCase().includes(query);
    });
  }, [accounts, search]);

  if (user?.role !== "admin") return null;

  return (
    <div data-testid="account-genitori-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Account Genitori</h1>
          <p className="text-sm text-slate-500 font-semibold">Adulti registrati e Animati collegati</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-2 text-sm font-extrabold text-emerald-700 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> {accounts.length} account
        </div>
      </div>

      <div className="card-app p-4 mb-5 flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><UsersRound className="h-5 w-5" /></div>
        <p className="text-xs text-slate-600 font-semibold">Gli account Genitore sono attivi subito. Da qui puoi controllare i dati dell'adulto, i figli collegati e lo stato delle richieste di iscrizione.</p>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input type="search" className="input-app !pl-11" placeholder="Cerca genitore o Animato..." value={search} onChange={(event) => setSearch(event.target.value)} data-testid="account-genitori-search" />
      </div>

      {error && <div className="badge-neg block mb-4 text-center py-3">{error}</div>}
      {loading ? <p className="text-center text-slate-400 font-semibold py-12">Caricamento...</p> : filtered.length === 0 ? (
        <Empty icon={UsersRound} text={search ? "Nessun account trovato" : "Nessun account Genitore"} sub={search ? "Prova con un altro nome o indirizzo email" : "Gli account creati dai genitori compariranno qui"} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((account, index) => (
            <motion.button
              type="button"
              key={account.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.03, 0.4) }}
              onClick={() => setSelected(account)}
              className="card-app p-4 text-left hover:-translate-y-1 hover:shadow-lg transition-all"
              data-testid={`account-genitore-card-${account.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><UsersRound className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-extrabold text-slate-900 truncate">{account.nome} {account.cognome}</p>
                  <p className="text-xs text-slate-500 font-semibold truncate">{account.email || "Email non indicata"}</p>
                  <p className="text-xs text-slate-400 font-semibold mt-0.5">{RELAZIONI[account.relazione] || "Genitore/tutore"}</p>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-slate-500 inline-flex items-center gap-1.5"><Baby className="h-3.5 w-3.5 text-sky-500" /> {(account.animati || []).length} {(account.animati || []).length === 1 ? "Animato" : "Animati"}</span>
                {(account.richieste || []).some((request) => request.stato === "in_attesa") && <span className="badge-warn !text-[10px]">Richiesta in attesa</span>}
              </div>
            </motion.button>
          ))}
        </div>
      )}

      <BottomSheet
        open={!!selected}
        onOpenChange={(open) => { if (!open) setSelected(null); }}
        title={selected ? `${selected.nome} ${selected.cognome}` : ""}
        description={selected?.email || "Account Genitore"}
        testId="account-genitore-detail-sheet"
      >
        {selected && <AccountGenitoreDetail account={selected} />}
      </BottomSheet>
    </div>
  );
}

function AccountGenitoreDetail({ account }) {
  const requests = account.richieste || [];
  return (
    <div className="space-y-5 pb-2">
      <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4 grid sm:grid-cols-2 gap-3">
        <Info icon={Mail} label="Email" value={account.email || "—"} />
        <Info icon={Phone} label="Telefono" value={account.telefono || "—"} />
        <Info icon={UsersRound} label="Relazione" value={RELAZIONI[account.relazione] || "Genitore/tutore"} />
        <Info icon={CalendarDays} label="Account creato" value={account.created_at ? fmtData(account.created_at.slice(0, 10)) : "—"} />
      </div>

      <section>
        <div className="flex items-center justify-between gap-2 mb-3"><h2 className="font-display font-extrabold text-slate-900">Animati associati</h2><span className="badge-info">{(account.animati || []).length}</span></div>
        {(account.animati || []).length === 0 ? <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500 font-semibold">Nessun Animato ancora collegato.</p> : (
          <div className="space-y-2">
            {account.animati.map((child) => <div key={child.link_id || child.id} className="rounded-2xl border border-slate-100 p-3 flex items-center gap-3" data-testid={`account-genitore-child-${child.id}`}>
              <div className="h-9 w-9 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center"><Baby className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1"><p className="font-bold text-slate-800 truncate">{child.nome} {child.cognome}</p><p className="text-xs text-slate-500 font-semibold">{child.squadra?.nome || "Squadra non assegnata"} · {child.relazione || "Figlio/a"}</p></div>
              <span className={child.quota_pagata ? "badge-pos !text-[10px]" : "badge-warn !text-[10px]"}>{child.quota_pagata ? "Quota pagata" : "Quota da verificare"}</span>
            </div>)}
          </div>
        )}
      </section>

      {requests.length > 0 && <section><h2 className="font-display font-extrabold text-slate-900 mb-3">Storico richieste</h2><div className="space-y-2">{requests.map((request) => { const [label, badge] = statoRichiesta[request.stato] || [request.stato || "—", "badge-info"]; return <div key={request.id} className="rounded-2xl border border-slate-100 p-3 flex items-center gap-3"><div className="min-w-0 flex-1"><p className="font-bold text-slate-800">{request.nome || "Animato"} {request.cognome || ""}</p><p className="text-xs text-slate-400 font-semibold">{request.created_at ? fmtData(request.created_at.slice(0, 10)) : ""}</p></div><span className={badge}>{label}</span></div>; })}</div></section>}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return <div className="min-w-0"><p className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-slate-400 flex items-center gap-1.5"><Icon className="h-3 w-3" /> {label}</p><p className="text-sm font-bold text-slate-700 truncate mt-1">{value}</p></div>;
}
